import { useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Icon } from '@ndrc/ui';
import type { Clock } from '../lib/clock';
import { formatParisTime } from '../lib/clock';
import { logout } from '../lib/rpc';
import { clearPracticeSession } from '../practice/usePracticeRequest';
import { useDesk } from './context';
import { t } from '../messages';
import { NotificationsBell } from './NotificationsBell';

/**
 * La barre de menu : marque, nom de l'app active, puis à droite l'utilisateur et l'horloge —
 * **dans cet ordre**, l'horloge tout à droite (docs/design/desktop-reference.md, § « Barre de
 * menu », corrigé le 6 septembre : ne réintroduis pas l'ancien ordre où l'horloge précédait le
 * nom).
 *
 * Les quatre menus « Fichier / Édition / Affichage / Aide » de la maquette d'origine ne sont pas
 * rendus : aucun n'aurait le moindre contenu aujourd'hui, et quatre contrôles qui n'en sont pas,
 * en permanence en haut de l'écran, sont pires que leur absence.
 *
 * Le sélecteur de classe n'apparaît **que** si l'utilisateur en a réellement plusieurs (le cas
 * d'un enseignant). Le cœur refuse de choisir une classe au hasard — `desk.apps` lève
 * `VALIDATION_FAILED` plutôt que de deviner ; ce sélecteur est la réponse honnête à ce refus.
 *
 * Le nom de l'utilisateur ouvre un menu — « Mon compte » et « Se déconnecter » au minimum, ce que
 * l'enseignant a demandé le premier jour où il a utilisé le bureau. Le panneau reprend la matière
 * du dock (même translucidité, même rayon) : il doit avoir l'air d'appartenir à cette maquette,
 * pas d'y être ajouté après coup.
 */
export interface MenuBarProps {
  clock: Clock;
  activeTitle: string;
  userName: string;
  classes: ReadonlyArray<{ id: string; name: string }>;
  selectedClassId: string | undefined;
  onSelectClass: (classId: string) => void;
  onOpenProfile: () => void;
  fullscreen: boolean;
  onToggleFullscreen: () => void;
}

export function MenuBar({
  clock,
  activeTitle,
  userName,
  classes,
  selectedClassId,
  onSelectClass,
  onOpenProfile,
  fullscreen,
  onToggleFullscreen,
}: MenuBarProps): JSX.Element {
  const { me } = useDesk();
  const [now, setNow] = useState(() => clock.now());
  useEffect(() => {
    const id = setInterval(() => {
      setNow(clock.now());
    }, 15_000);
    return () => {
      clearInterval(id);
    };
  }, [clock]);

  const [menuOpen, setMenuOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Un clic ailleurs, ou Échap, referme le menu — le comportement attendu de n'importe quel menu.
  useEffect(() => {
    if (!menuOpen) return;
    const onPointerDown = (event: PointerEvent): void => {
      if (menuRef.current?.contains(event.target as Node) === false) setMenuOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  async function onLogout(): Promise<void> {
    setLoggingOut(true);
    try {
      await logout();
    } finally {
      if (me.role !== 'student') clearPracticeSession(me.id);
      queryClient.clear();
      setMenuOpen(false);
      await navigate('/login', { replace: true });
    }
  }

  return (
    <div className="menubar">
      <span className="brand" aria-hidden="true">
        <svg width="9" height="9" viewBox="0 0 10 10" fill="currentColor" focusable="false">
          <path d="M5 1.1 8.9 5 5 8.9 1.1 5z" />
        </svg>
      </span>
      <span className="menubar-app">{activeTitle}</span>
      <span className="menubar-spacer" />
      {classes.length > 1 ? (
        <label className="menubar-class">
          {t('menubar.classLabel')}
          <select
            value={selectedClassId ?? ''}
            onChange={(event) => {
              onSelectClass(event.target.value);
            }}
          >
            {classes.map((klass) => (
              <option key={klass.id} value={klass.id}>
                {klass.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <button
        type="button"
        className="menubar-fullscreen"
        aria-pressed={fullscreen}
        aria-label={fullscreen ? t('window.exitFullscreen') : t('window.fullscreen')}
        title={fullscreen ? t('window.exitFullscreen') : t('window.fullscreen')}
        onClick={onToggleFullscreen}
      >
        <Icon name={fullscreen ? 'collapse' : 'expand'} size={13} strokeWidth={1.7} />
      </button>
      {/* La cloche, juste avant le nom : c'est le seul endroit du bureau qui ne quitte jamais
          l'écran, et une notification qui n'est pas vue n'existe pas. Pour tout le monde — une
          notification est une donnée personnelle, jamais une affaire de rôle. */}
      <NotificationsBell />
      <div className="menubar-user-wrap" ref={menuRef}>
        <button
          type="button"
          className="menubar-user"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={() => {
            setMenuOpen((open) => !open);
          }}
        >
          {userName}
          <Icon name="chevron-right" size={9} strokeWidth={2} className="menubar-user-caret" />
        </button>
        {menuOpen ? (
          <div className="user-menu" role="menu">
            <button
              type="button"
              role="menuitem"
              className="user-menu-item"
              onClick={() => {
                setMenuOpen(false);
                onOpenProfile();
              }}
            >
              <Icon name="user" size={15} strokeWidth={1.7} />
              {t('dock.profile')}
            </button>
            <button
              type="button"
              role="menuitem"
              className="user-menu-item"
              disabled={loggingOut}
              onClick={() => {
                void onLogout();
              }}
            >
              <Icon name="logout" size={15} strokeWidth={1.7} />
              {loggingOut ? t('profile.logoutBusy') : t('profile.logout')}
            </button>
          </div>
        ) : null}
      </div>
      <span className="menubar-clock">{formatParisTime(now)}</span>
    </div>
  );
}
